import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getAppointmentDate, getAppointmentTimeFrom } from '../../../api/created/appointment.ts';
import {
  getGetLabOrdersByPatientQueryKey,
  useCreateTuberculinTest,
  useUpdateTuberculinTest,
} from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AppointmentDto,
  CreateQuantiferonTestDtoInterpretation,
  CreateTuberculinTestDto,
  CreateTuberculinTestDtoInterpretation,
  LabOrderDtoType,
} from '../../../api/generated/model';
import { queryClient } from '../../../api/queryClient.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import {
  formatDateForInput,
  formatTimeForInput,
  getDefaultValueForDateInput,
  getDefaultValueForLocalTimeInput,
} from '../../../utils/date.ts';
import { toLabOrderSamplingContact } from '../../../utils/labOrder.ts';
import { stringToNumberOrUndefined } from '../../../utils/number.ts';
import { getStringOrUndefined } from '../../../utils/string.ts';
import { DeleteLabOrderButton } from './DeleteLabOrderButton.tsx';
import { LabOrderDetailsForm } from './LabOrderDetailsForm.tsx';
import { ResultInterpretationButtonGroup } from './ResultInterpretationButtonGroup.tsx';

interface FormValues {
  samplingDate: string;
  samplingTime: string;
  samplingAddress: AddressDto | null;
  samplingContact: AddressContactDto | null;
  indurationSize: string;
  readDate: string;
  readTime: string;
  interpretation: CreateQuantiferonTestDtoInterpretation | null;
  notes: string;
  isTodo: boolean;
}

export function CreateTuberculinResultForm({
  appointment,
  patientId,
  onClose,
  currentResult,
  currentResultId,
}: {
  appointment?: AppointmentDto | null;
  patientId: string;
  onClose: () => void;
  currentResult?: CreateTuberculinTestDto;
  currentResultId?: string;
}) {
  const isEditing = !!currentResultId;

  const formMethods = useForm<FormValues>({
    defaultValues: getDefaultValues(appointment, currentResult),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = formMethods;

  const isSamplingAddressDisabled = !!currentResult?.samplingAddress;
  const isSamplingContactDisabled = !!currentResult?.samplingContact;
  const isIndurationSizeRequired = !!currentResult?.indurationSize;
  const isInterpretationRequired =
    !!currentResult?.interpretation && currentResult.interpretation !== CreateTuberculinTestDtoInterpretation.PENDING;
  const isReadDateRequired = !!currentResult?.readTime;
  const isReadTimeRequired = !!watch('readDate');

  const createTuberculinTestMutation = useCreateTuberculinTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim speichern des Testresultates ist ein Fehler aufgetreten'),
    },
  });

  const updateTuberculinTestMutation = useUpdateTuberculinTest({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLabOrdersByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Aktualisieren des Testresultats ist ein Fehler aufgetreten'),
    },
  });

  const handleOnSubmit = (data: FormValues) => {
    const createTuberculinTestDto: CreateTuberculinTestDto = {
      appointmentId: appointment?.id,
      patientId: patientId,
      samplingDate: data.samplingDate,
      samplingTime: getStringOrUndefined(data.samplingTime),
      samplingAddress: data.samplingAddress ?? undefined,
      samplingContact: toLabOrderSamplingContact(data.samplingContact),
      indurationSize: stringToNumberOrUndefined(data.indurationSize),
      interpretation: data.interpretation ?? CreateTuberculinTestDtoInterpretation.PENDING,
      readTime: data.readDate && data.readTime && data.readDate + 'T' + data.readTime,
      notes: getStringOrUndefined(data.notes),
      isTodo: data.isTodo,
    };
    if (isEditing) {
      updateTuberculinTestMutation.mutate({ id: currentResultId, data: createTuberculinTestDto });
    } else {
      createTuberculinTestMutation.mutate({ data: createTuberculinTestDto });
    }
  };

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Stack gap={2}>
          <Typography level={'title-lg'}>Tuberkulintest</Typography>
          <LabOrderDetailsForm
            samplingDate={'samplingDate'}
            samplingTime={'samplingTime'}
            samplingAddress={'samplingAddress'}
            samplingContact={'samplingContact'}
            isSamplingAddressDisabled={isSamplingAddressDisabled}
            isSamplingContactDisabled={isSamplingContactDisabled}
            isEditing={isEditing}
          />
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Resultat</Typography>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.indurationSize}>
              <FormLabel>
                <Typography>Befund in mm {isIndurationSizeRequired && '*'}</Typography>
              </FormLabel>
              <Input type={'number'} {...register('indurationSize', { required: isIndurationSizeRequired })} />
              {errors.indurationSize && <FormHelperText>Befund in mm muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.interpretation}>
              <FormLabel>
                <Typography>Resultat {isInterpretationRequired && '*'}</Typography>
              </FormLabel>
              <Controller
                name={'interpretation'}
                control={control}
                rules={{ required: isInterpretationRequired }}
                render={({ field: { onChange, value } }) => (
                  <ResultInterpretationButtonGroup
                    value={value}
                    onChange={onChange}
                    resultOptions={[
                      CreateQuantiferonTestDtoInterpretation.POSITIVE,
                      CreateQuantiferonTestDtoInterpretation.NEGATIVE,
                      CreateQuantiferonTestDtoInterpretation.INDETERMINATE,
                    ]}
                  />
                )}
              />
              {errors.interpretation && <FormHelperText>Resultat muss angegeben werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.readDate}>
              <FormLabel>
                <Typography>Datum {isReadDateRequired && '*'}</Typography>
              </FormLabel>
              <GermanDateInput
                control={control}
                name="readDate"
                rules={{
                  required: isReadDateRequired,
                }}
              />
              {errors.readDate && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
            </FormControl>
            <FormControl sx={{ flex: 1 }} error={!!errors.readTime}>
              <FormLabel>
                <Typography>Zeit {isReadTimeRequired && '*'}</Typography>
              </FormLabel>
              <Input {...register('readTime', { required: isReadTimeRequired })} type={'time'} />
              {errors.readTime && <FormHelperText>Zeit muss ausgefüllt werden</FormHelperText>}
            </FormControl>
          </Stack>
          <Divider inset={'context'} />
          <Typography level={'title-lg'}>Notiz</Typography>
          <Textarea {...register('notes')} minRows={2} maxRows={4} />
          {!isEditing && (
            <Controller
              name="isTodo"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  endDecorator={'Handlung erforderlich (ist ToDo)'}
                  sx={{ paddingTop: '1rem', alignSelf: 'flex-start' }}
                />
              )}
            />
          )}
          <FormActionButtons
            onAbortClick={handleOnClose}
            disableSubmit={!isFormDirty}
            isLoading={createTuberculinTestMutation.isPending || updateTuberculinTestMutation.isPending}>
            {currentResultId && (
              <DeleteLabOrderButton
                orderId={currentResultId}
                patientId={patientId}
                appointmentId={currentResult?.appointmentId}
                orderType={LabOrderDtoType.TUBERCULIN}
              />
            )}
          </FormActionButtons>
        </Stack>
      </form>
    </FormProvider>
  );
}

function getDefaultValues(appointment?: AppointmentDto | null, currentResult?: CreateTuberculinTestDto): FormValues {
  return {
    samplingDate: getDefaultValueForDateInput(
      currentResult?.samplingDate,
      getAppointmentDate(appointment) ?? undefined,
    ),
    samplingTime: currentResult?.samplingTime
      ? getDefaultValueForLocalTimeInput(currentResult.samplingTime)
      : (getAppointmentTimeFrom(appointment) ?? ''),
    samplingAddress: currentResult?.samplingAddress ?? null,
    samplingContact: currentResult?.samplingContact ?? null,
    indurationSize: currentResult?.indurationSize?.toString() ?? '',
    readDate: currentResult?.readTime ? formatDateForInput(currentResult.readTime) : '',
    readTime: currentResult?.readTime ? formatTimeForInput(currentResult.readTime) : '',
    interpretation:
      currentResult?.interpretation === CreateTuberculinTestDtoInterpretation.PENDING
        ? null
        : (currentResult?.interpretation ?? null),
    notes: currentResult?.notes ?? '',
    isTodo: currentResult?.isTodo ?? false,
  };
}
